"use client";

import Image from "@/components/ui/OptimizedImage";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type Performer = {
  id: string;
  name: string;
  bio: string | null;
  imageUrl: string | null;
  socialLinks: Record<string, string> | null;
  role: string | null;
  performanceStartTime: string | null;
  performanceEndTime: string | null;
  isHeadliner: boolean;
  stageId: string | null;
};

export type Stage = {
  id: string;
  name: string;
  description: string | null;
};

export interface LineupSectionProps {
  sortedPerformers: Performer[];
  stages: Stage[];
  expandedPerformerId: string | null;
  setExpandedPerformerId: (id: string | null) => void;
  showAllPerformers: boolean;
  setShowAllPerformers: (show: boolean) => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function performerInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}

function formatPerformerTime(start: string | null, end: string | null): string | null {
  if (!start) return null;
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return null;
  const startStr = startDate.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });
  if (!end) return `From ${startStr}`;
  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) return `From ${startStr}`;
  const endStr = endDate.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });
  return `${startStr} \u2013 ${endStr}`;
}

function socialIconName(key: string): string {
  const lower = key.toLowerCase();
  if (lower === "instagram") return "Instagram";
  if (lower === "spotify") return "Spotify";
  if (lower === "soundcloud") return "SoundCloud";
  if (lower === "youtube") return "YouTube";
  if (lower === "twitter" || lower === "x") return "X";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function PinIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function SocialIcon({ platform, className = "h-3.5 w-3.5" }: { platform: string; className?: string }) {
  const lower = platform.toLowerCase();
  if (lower === "instagram") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (lower === "spotify") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 15c3-1 6-1 9 .5M7.5 12.5c3.5-1.2 7.5-1 10.5.8M7 10c4-1.5 9-1 12.5 1" />
      </svg>
    );
  }
  if (lower === "youtube") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2" y="4" width="20" height="16" rx="4" />
        <path d="M10 9l5 3-5 3V9z" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (lower === "twitter" || lower === "x") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M4 4l7.2 8.4M20 20l-7.2-8.4m0 0L20 4M11.2 12.4L4 20" />
      </svg>
    );
  }
  if (lower === "soundcloud") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M2 16v-3M5 16V9M8 16V7M11 16V5M14 16V8M17 16c2 0 4-1.5 4-4s-2-4-4-4c-.5 0-1 .1-1.5.2" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LineupSection({
  sortedPerformers,
  stages,
  expandedPerformerId,
  setExpandedPerformerId,
  showAllPerformers,
  setShowAllPerformers,
}: LineupSectionProps) {
  if (sortedPerformers.length === 0) return null;

  const hasMultiStage = stages.length > 1;

  const getStageName = (stageId: string | null) => {
    if (!stageId) return null;
    return stages.find((s) => s.id === stageId)?.name ?? null;
  };

  return (
    <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
      <div className="flex items-center justify-between mb-5">
        <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">
          {sortedPerformers.length === 1 ? "Featured Artist" : "Lineup"}
        </span>
        <span className="text-xs text-white/50">{sortedPerformers.length} artist{sortedPerformers.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-3">
        {(showAllPerformers ? sortedPerformers : sortedPerformers.slice(0, 6)).map((p) => {
          const timeStr = formatPerformerTime(p.performanceStartTime, p.performanceEndTime);
          const stageName = hasMultiStage && p.stageId ? getStageName(p.stageId) : null;
          return (
            <div
              key={p.id}
              onClick={() => setExpandedPerformerId(expandedPerformerId === p.id ? null : p.id)}
              className={`flex items-center gap-4 p-4 rounded-[14px] bg-white/[0.04] border cursor-pointer transition hover:translate-x-[3px] ${
                p.isHeadliner
                  ? "border-[var(--hopp-sapphire)] bg-gradient-to-br from-[rgba(58,92,255,0.08)] to-transparent"
                  : "border-white/[0.08] hover:border-white/[0.12]"
              }`}
            >
              <div className={`relative w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0 border-2 ${p.isHeadliner ? "border-[var(--hopp-sapphire)]" : "border-white/[0.08]"}`}>
                {p.imageUrl ? (
                  <Image src={p.imageUrl} alt={p.name} fill className="object-cover" sizes="60px" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-white/[0.04] text-sm font-bold uppercase text-white/20">
                    {performerInitials(p.name)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                {p.isHeadliner ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#4A8BFF] mb-1">
                    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                    Headliner
                  </span>
                ) : null}
                <p className="font-medium text-[15px] text-white">{p.name}</p>
                {p.role ? <p className="text-xs text-white/50 mt-0.5">{p.role}</p> : null}
                {stageName ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#4A8BFF] mt-1 tracking-[0.02em]">
                    <PinIcon className="h-3 w-3" />
                    {stageName}
                  </span>
                ) : null}
                {expandedPerformerId === p.id && p.bio ? (
                  <p className="mt-2 text-xs leading-relaxed text-white/50">{p.bio}</p>
                ) : null}
                {expandedPerformerId === p.id && p.socialLinks && Object.keys(p.socialLinks).length > 0 ? (
                  <div className="mt-2 flex items-center gap-3">
                    {Object.entries(p.socialLinks).map(([platform, url]) => (
                      <a key={platform} href={url} target="_blank" rel="noreferrer" aria-label={`${p.name} on ${socialIconName(platform)}`} className="flex h-8 w-8 items-center justify-center rounded-full text-white/50 transition hover:text-white bg-white/[0.04]" onClick={(e) => e.stopPropagation()}>
                        <SocialIcon platform={platform} className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
              {timeStr ? (
                <span className="font-medium text-[11px] text-white/60 px-2.5 py-1 rounded-md bg-[#0c0c18] whitespace-nowrap tracking-[0.04em]">
                  {timeStr}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {sortedPerformers.length > 6 && !showAllPerformers ? (
        <button type="button" onClick={() => setShowAllPerformers(true)} className="mt-4 text-[#4A8BFF] text-[13px] font-medium hover:underline">
          View all {sortedPerformers.length} artists
        </button>
      ) : null}
    </div>
  );
}
